import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { useAuth } from "../contexts/AuthContext";
import { AltalabLogo } from "../components/AltalabLogo";
import BottomNavigation from "../components/BottomNavigation";
import {
  Users,
  RefreshCw,
  ShoppingBag,
  ArrowRight,
  CheckCircle,
  MessageCircle,
  Calendar,
  Bell,
  Settings,
  BarChart3,
} from "lucide-react";

const HomePage = () => {
  const navigate = useNavigate();
  const { user } = useAuth();

  const pillars = [
    {
      id: "marketplace",
      title: "Divulgação Paga",
      description: "Compre ou venda serviços de divulgação",
      icon: ShoppingBag,
      color: "from-violet-500 to-violet-600",
      borderColor: "border-violet-500/30",
      features: ["Venda sua audiência", "Compre divulgação", "Várias plataformas"],
      cta: "Ver Marketplace",
      path: "/marketplace",
    },
    {
      id: "exchanges",
      title: "Trocas Gratuitas",
      description: "Divulgue e seja divulgado sem pagar nada",
      icon: RefreshCw,
      color: "from-emerald-500 to-emerald-600",
      borderColor: "border-emerald-500/30",
      features: ["100% gratuito", "Baseado em alcance", "Conexão prévia"],
      cta: "Ver Minhas Trocas",
      path: "/exchanges",
      badge: "GRÁTIS",
    },
  ];

  const isSeller = user?.interests?.includes("sell");

  const quickActions = [
    ...(isSeller ? [{ icon: BarChart3, label: "Dashboard", path: "/dashboard", count: null }] : []),
    { icon: Users, label: "Conexões", path: "/connections", count: 0 },
    { icon: Calendar, label: "Agenda", path: "/agenda", count: 0 },
    { icon: Bell, label: "Notificações", path: "/notifications", count: 0 },
    ...(!isSeller ? [{ icon: Settings, label: "Config", path: "/profile", count: null }] : []),
  ];

  return (
    <div className="min-h-screen bg-[#080808] text-white pb-20">
      {/* Header */}
      <header className="sticky top-0 z-40 px-4 py-3 bg-[#080808]/90 backdrop-blur-md border-b border-white/5">
        <div className="flex items-center justify-between max-w-lg mx-auto">
          <AltalabLogo size="small" clickable={false} />
          <div className="flex items-center gap-2">
            <span className="text-sm text-white/60">Olá,</span>
            <span className="text-sm font-medium">{user?.display_name?.split(' ')[0] || 'Criador'}</span>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="px-4 py-6 max-w-lg mx-auto">
        {/* Welcome Section */}
        <motion.div
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          className="mb-6"
        >
          <h1 className="text-xl font-bold mb-1">Como quer crescer hoje?</h1>
          <p className="text-sm text-white/50">Escolha seu caminho</p>
        </motion.div>

        {/* 3 Pillars */}
        <div className="space-y-4 mb-8">
          {pillars.map((pillar, index) => {
            const Icon = pillar.icon;
            return (
              <motion.div
                key={pillar.id}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: index * 0.1 }}
                onClick={() => navigate(pillar.path)}
                className={`relative bg-white/5 border ${pillar.borderColor} rounded-2xl p-4 cursor-pointer active:scale-[0.98] transition-transform`}
                data-testid={`pillar-${pillar.id}`}
              >
                {pillar.badge && (
                  <span className="absolute top-3 right-3 px-2 py-0.5 bg-emerald-500/20 border border-emerald-500/30 rounded-full text-[10px] font-semibold text-emerald-400">
                    {pillar.badge}
                  </span>
                )}
                
                <div className="flex items-start gap-4">
                  <div className={`w-12 h-12 rounded-xl bg-gradient-to-br ${pillar.color} flex items-center justify-center flex-shrink-0`}>
                    <Icon className="w-6 h-6 text-white" />
                  </div>
                  
                  <div className="flex-1 min-w-0">
                    <h3 className="font-semibold mb-1">{pillar.title}</h3>
                    <p className="text-xs text-white/50 mb-3">{pillar.description}</p>
                    
                    <div className="flex flex-wrap gap-2 mb-3">
                      {pillar.features.map((feature, i) => (
                        <span key={i} className="flex items-center gap-1 text-[10px] text-white/60">
                          <CheckCircle className="w-3 h-3 text-emerald-400" />
                          {feature}
                        </span>
                      ))}
                    </div>
                    
                    <div className="flex items-center text-sm font-medium text-[#FD5564]">
                      {pillar.cta}
                      <ArrowRight className="w-4 h-4 ml-1" />
                    </div>
                  </div>
                </div>
              </motion.div>
            );
          })}
        </div>

        {/* Quick Actions */}
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.3 }}
        >
          <h2 className="text-sm font-semibold text-white/70 mb-3">Acesso Rápido</h2>
          <div className="grid grid-cols-4 gap-2">
            {quickActions.map((action) => {
              const Icon = action.icon;
              return (
                <button
                  key={action.path}
                  onClick={() => navigate(action.path)}
                  className="flex flex-col items-center justify-center p-3 bg-white/5 border border-white/10 rounded-xl hover:bg-white/10 transition-colors"
                  data-testid={`quick-${action.label.toLowerCase()}`}
                >
                  <div className="relative">
                    <Icon className="w-5 h-5 text-white/70 mb-1" />
                    {action.count !== null && action.count > 0 && (
                      <span className="absolute -top-1 -right-1 w-4 h-4 bg-[#FD5564] rounded-full text-[10px] flex items-center justify-center">
                        {action.count}
                      </span>
                    )}
                  </div>
                  <span className="text-[10px] text-white/50">{action.label}</span>
                </button>
              );
            })}
          </div>
        </motion.div>
      </main>

      {/* Bottom Navigation */}
      <BottomNavigation />
    </div>
  );
};

export default HomePage;
